import { useState, useRef, useCallback, useEffect } from 'react';
import { hapticFeedback } from '@tma.js/sdk';

const safeHaptic = (callback: () => void) => {
  try { callback(); } catch {}
};

export interface UseLongPressMenuOptions {
  items: string[];
  onSelect: (item: string | null) => void;
  longPressDelay?: number;
  itemHeight?: number; // высота одного пункта меню (в пикселях)
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const useLongPressMenu = ({
  items,
  onSelect,
  longPressDelay = 300,
  itemHeight = 56, // подбираем под ваш дизайн
}: UseLongPressMenuOptions) => {
  const [isActivated, setIsActivated] = useState(false);
  const [buttonRect, setButtonRect] = useState<Rect | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const highlightedIndexRef = useRef(-1);
  const longPressTimerRef = useRef<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isLongPressTriggered = useRef(false);

  const clearTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const commitSelection = useCallback(() => {
    if (!isActivated) return;
    const idx = highlightedIndexRef.current;
    if (idx >= 0 && idx < items.length) {
      onSelect(items[idx]);
    } else {
      onSelect(null); // главная
    }
    setIsActivated(false);
    isLongPressTriggered.current = false;
    setHighlightedIndex(-1);
    highlightedIndexRef.current = -1;
    setButtonRect(null);
    clearTimer();
  }, [isActivated, items, onSelect, clearTimer]);

  // Глобальные слушатели движения и отпускания
  useEffect(() => {
    if (!isActivated || !buttonRect) return;

    // Вычисляем верхнюю границу меню (меню находится НАД кнопкой)
    const menuHeight = items.length * itemHeight;
    const menuTop = buttonRect.top - menuHeight - 16; // 16px отступ от кнопки

    const handleGlobalMove = (e: MouseEvent | TouchEvent) => {
      const clientY = 'touches' in e ? (e as TouchEvent).touches[0]?.clientY : (e as MouseEvent).clientY;
      if (clientY === undefined) return;
      const relativeY = clientY - menuTop;
      const index = Math.floor(relativeY / itemHeight);
      const clampedIndex = Math.max(0, Math.min(items.length - 1, index));
      setHighlightedIndex(clampedIndex);
      highlightedIndexRef.current = clampedIndex;
    };

    const handleGlobalUp = () => {
      // При отпускании фиксируем выбор
      commitSelection();
      // Удаляем слушатели
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('mouseup', handleGlobalUp);
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalUp);
    };

    document.addEventListener('mousemove', handleGlobalMove);
    document.addEventListener('mouseup', handleGlobalUp);
    document.addEventListener('touchmove', handleGlobalMove, { passive: false } as AddEventListenerOptions);
    document.addEventListener('touchend', handleGlobalUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('mouseup', handleGlobalUp);
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalUp);
    };
  }, [isActivated, buttonRect, items, itemHeight, commitSelection]);

  const setHighlighted = useCallback((index: number) => {
    setHighlightedIndex(index);
    highlightedIndexRef.current = index;
  }, []);

  const onPressStart = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
    }
    setHighlighted(-1);
    isLongPressTriggered.current = false;

    longPressTimerRef.current = window.setTimeout(() => {
      isLongPressTriggered.current = true;
      setIsActivated(true);
      safeHaptic(() => hapticFeedback.impactOccurred("heavy"));
    }, longPressDelay);
  }, [longPressDelay, setHighlighted]);

  const onPressEnd = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  const onCancel = useCallback(() => {
    clearTimer();
    if (isLongPressTriggered.current) {
      setIsActivated(false);
      isLongPressTriggered.current = false;
      setHighlightedIndex(-1);
      highlightedIndexRef.current = -1;
      setButtonRect(null);
    }
  }, [clearTimer]);

  return {
    isActivated,
    highlightedIndex,
    buttonRect,
    buttonProps: {
      ref: buttonRef,
      onTouchStart: onPressStart,
      onMouseDown: onPressStart,
      onTouchEnd: onPressEnd,
      onMouseUp: onPressEnd,
      onTouchCancel: onCancel,
      onMouseLeave: onCancel,
    },
  };
};